"use client";

import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, MapPin, Search, ChartBar as BarChart3, Target, ArrowRight, ShieldCheck, Compass, CircleCheck as CheckCircle2, Building2, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
const marketStats = [{
  label: "Doncaster Monthly Search Volume",
  value: "32,400+",
  badge: "+18% YoY"
}, {
  label: "Local Map Pack Lead Share",
  value: "64%",
  badge: "High Commercial"
}, {
  label: "Avg Doncaster SEO Difficulty",
  value: "32 / 100",
  badge: "High Opportunity"
}, {
  label: "B2B Logistics & Tech Growth",
  value: "+27%",
  badge: "South Yorkshire"
}];
const keywordTrends = [{
  keyword: "SEO consultant Doncaster",
  volume: "480 / mo",
  intent: "High Commercial",
  competition: "Medium",
  opportunity: "Primary targeted query for local commercial SEO partnerships"
}, {
  keyword: "Logistics services Doncaster",
  volume: "2,400 / mo",
  intent: "Transactional",
  competition: "High",
  opportunity: "iPort & transport hub targeting for B2B industrial searchers"
}, {
  keyword: "Commercial solicitors Doncaster",
  volume: "880 / mo",
  intent: "High Commercial",
  competition: "Medium",
  opportunity: "Google Business Profile & schema service optimization"
}, {
  keyword: "Engineering suppliers South Yorkshire",
  volume: "1,600 / mo",
  intent: "Commercial",
  competition: "Low-Medium",
  opportunity: "Technical SEO & long-tail content hub strategy"
}, {
  keyword: "B2B IT support Doncaster",
  volume: "720 / mo",
  intent: "Transactional",
  competition: "Medium",
  opportunity: "Dedicated landing page architecture & authority building"
}];
const neighboringCities = [{
  name: "Wakefield",
  path: "/LocalSeoConsultantWakefieldPage",
  distance: "21 miles",
  focus: "Manufacturing & B2B Lead Gen"
}, {
  name: "Hull",
  path: "/LocalSeoConsultantHullPage",
  distance: "45 miles",
  focus: "Maritime, Logistics & Commerce"
}, {
  name: "Derby",
  path: "/LocalSeoConsultantDerbyPage",
  distance: "52 miles",
  focus: "Engineering & Advanced Tech"
}, {
  name: "Northampton",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "95 miles",
  focus: "Distribution & Industrial SEO"
}, {
  name: "Stoke-on-Trent",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  distance: "70 miles",
  focus: "Local Services & Trade SEO"
}, {
  name: "Warrington",
  path: "/LocalSeoConsultantWarringtonPage",
  distance: "62 miles",
  focus: "Commercial Hub Lead Gen"
}];
const quarterlyChartData = [{
  quarter: "Q1",
  Logistics: 1800,
  IndustrialTech: 950,
  ProfessionalServices: 1200
}, {
  quarter: "Q2",
  Logistics: 2100,
  IndustrialTech: 1100,
  ProfessionalServices: 1350
}, {
  quarter: "Q3",
  Logistics: 2450,
  IndustrialTech: 1380,
  ProfessionalServices: 1500
}, {
  quarter: "Q4",
  Logistics: 2900,
  IndustrialTech: 1620,
  ProfessionalServices: 1780
}];
const chartConfig = {
  Logistics: {
    label: "Logistics & Freight",
    color: "hsl(220, 80%, 30%)"
  },
  IndustrialTech: {
    label: "Industrial & Tech",
    color: "hsl(340, 75%, 65%)"
  },
  ProfessionalServices: {
    label: "Professional Services",
    color: "hsl(160, 60%, 40%)"
  }
};
const strategicPillars = [{
  title: "Doncaster Map Pack & Hyper-Local Visibility",
  desc: "I optimize your local Google Business Profile and geo-relevance indicators so your business dominates local search results across DN1 to DN12 postal sectors.",
  icon: MapPin
}, {
  title: "B2B Lead Generation for Industrial Hubs",
  desc: "Targeting high-intent buyers near iPort, Wheatley, and Armthorpe with tailor-made technical architecture and search funnel landing pages.",
  icon: Building2
}, {
  title: "AI Search & Modern SERP Positioning",
  desc: "Preparing your Doncaster brand for Google AI Overviews and modern search experiences to ensure your content is indexed, cited, and recommended.",
  icon: Zap
}];
export default function DoncasterMarket() {
  return <section id="doncastermarketintelligence" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 space-y-16 max-w-7xl">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-medium rounded-full">
            <Compass className="w-3.5 h-3.5 mr-1 text-primary" /> Doncaster Search Market Intelligence
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
            Doncaster Search Trends &amp; Local Keyword Opportunities
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            As an independent SEO strategy consultant, I analyze real-time Doncaster search behavior,
            competition levels, and local demand to craft high-impact search strategies. When you work with me,
            you get direct consultant expertise—no account managers or junior templates.
          </p>
        </div>

        {/* Top Market Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {marketStats.map((stat, i) => <motion.div key={stat.label} data-index={i} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }} viewport={{
          once: true
        }}>
              <Card className="border border-border/80 bg-card hover:border-primary/40 transition-colors shadow-sm h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardDescription className="text-xs font-medium">{stat.label}</CardDescription>
                    <Badge variant="outline" className="text-2xs font-semibold">
                      {stat.badge}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl md:text-3xl font-bold text-foreground font-default">
                    {stat.value}
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Search Growth & Strategic Pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Chart Card */}
          <Card className="lg:col-span-7 border border-border bg-card shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-bold font-default flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" /> Sector Search Growth in Doncaster
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Quarterly high-intent search query volume across Doncaster key commercial sectors.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <ChartContainer config={chartConfig} className="min-h-[280px] w-full">
                <BarChart data={quarterlyChartData} margin={{
                top: 20,
                right: 10,
                left: -20,
                bottom: 0
              }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="quarter" tickLine={false} axisLine={false} className="text-xs" />
                  <YAxis tickLine={false} axisLine={false} className="text-xs" />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="Logistics" fill="var(--color-Logistics)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="IndustrialTech" fill="var(--color-IndustrialTech)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="ProfessionalServices" fill="var(--color-ProfessionalServices)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 text-xs text-muted-foreground justify-center">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-xs bg-primary inline-block" /> Logistics &amp; Freight
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-xs bg-accent inline-block" /> Industrial &amp; Tech
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-xs bg-chart-4 inline-block" /> Professional Services
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Pillars List */}
          <div className="lg:col-span-5 space-y-4">
            <div className="space-y-2">
              <Badge variant="outline" className="text-xs">
                Direct Expert Execution
              </Badge>
              <h3 className="text-2xl font-bold font-default text-foreground">
                How I Capture Doncaster Market Share For You
              </h3>
              <p className="text-sm text-muted-foreground">
                Generic regional strategies waste budget. I build customized Doncaster search campaigns designed to turn organic searchers into qualified leads.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              {strategicPillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <motion.div key={pillar.title} data-index={i} initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.3,
                delay: i * 0.1
              }} viewport={{
                once: true
              }}>
                    <div className="p-4 rounded-xl border border-border bg-card hover:bg-muted/40 transition-colors flex gap-4">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 h-fit">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-sm font-semibold font-default text-foreground">
                          {pillar.title}
                        </h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {pillar.desc}
                        </p>
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </div>

        {/* Keyword Table */}
        <Card className="border border-border bg-card shadow-sm overflow-hidden">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-xl font-bold font-default flex items-center gap-2">
                  <Search className="w-5 h-5 text-primary" /> Verified Doncaster Keyword Analysis
                </CardTitle>
                <CardDescription className="mt-1">
                  High-intent keyword opportunities identified for businesses competing in Doncaster and South Yorkshire.
                </CardDescription>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/SeoAuditsPage">
                  Request Custom Audit <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="font-semibold text-xs">Search Term / Keyword</TableHead>
                  <TableHead className="font-semibold text-xs">Est. Volume</TableHead>
                  <TableHead className="font-semibold text-xs">Search Intent</TableHead>
                  <TableHead className="font-semibold text-xs">SERP Competition</TableHead>
                  <TableHead className="font-semibold text-xs hidden md:table-cell">Strategic Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keywordTrends.map((row, i) => <TableRow key={row.keyword} data-index={i} className="hover:bg-muted/30">
                    <TableCell className="font-medium text-sm text-foreground">
                      <div className="flex items-center gap-2">
                        <Target className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span>{row.keyword}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{row.volume}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-2xs font-normal">
                        {row.intent}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-medium text-foreground">{row.competition}</TableCell>
                    <TableCell className="text-xs text-muted-foreground hidden md:table-cell">
                      {row.opportunity}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Regional Connections */}
        <div className="space-y-6 pt-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 border-b border-border pb-4">
            <div>
              <Badge variant="outline" className="text-xs mb-2">
                Yorkshire &amp; Regional Coverage
              </Badge>
              <h3 className="text-2xl font-bold font-default text-foreground">
                Regional SEO Coverage Around Doncaster
              </h3>
            </div>
            <p className="text-xs text-muted-foreground max-w-md">
              Expanding your commercial reach beyond South Yorkshire? Explore my dedicated local search analyses for adjacent growth markets.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringCities.map((city, i) => <motion.div key={city.name} data-index={i} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: i * 0.05
          }} viewport={{
            once: true
          }}>
                <Card className="border border-border bg-card hover:border-primary/50 transition-all hover:shadow-md group h-full">
                  <CardHeader className="p-4 pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold font-default group-hover:text-primary transition-colors">
                        {city.name} SEO
                      </CardTitle>
                      <span className="text-2xs text-muted-foreground font-mono">{city.distance}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 space-y-3">
                    <p className="text-xs text-muted-foreground">{city.focus}</p>
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between h-8 text-xs p-0 px-2 group-hover:bg-primary/10">
                      <Link to={city.path}>
                        View {city.name} Analysis <ArrowRight className="w-3 h-3 text-primary" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Bottom CTA Block */}
        <div className="rounded-2xl bg-muted/60 border border-border p-8 md:p-12 text-center space-y-6 relative overflow-hidden">
          <div className="max-w-2xl mx-auto space-y-3">
            <Badge variant="default" className="bg-primary text-primary-foreground">
              Direct Consultant Access
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
              Ready to Expand Your Search Market Share in Doncaster?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Speak directly with me, Yassine Essaadi. I will audit your existing search profile and build a bespoke, transparent SEO strategy tailored specifically to your growth targets.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">
                Explore My SEO Case Studies
              </Link>
            </Button>
          </div>

          <div className="pt-4 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-primary" /> No Agency Middlemen
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-primary" /> Tailored Strategic Deliverables
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-primary" /> Transparent Reporting
            </span>
          </div>
        </div>
      </div>
    </section>;
}